import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip } from "recharts";
import { Flame, Target, Clock, TrendingUp, Brain } from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/workout";
import { Skeleton } from "../components/ui/skeleton";

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5">
      <Icon size={20} className="text-brand-orange mb-3" />
      <p className="font-heading font-black text-3xl tracking-tight text-foreground">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

const CAT_COLOR = {
  recovery: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  volume: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
  intensity: "bg-brand-orange/15 text-brand-orange",
  schedule: "bg-purple-500/15 text-purple-600 dark:text-purple-400",
  preference: "bg-brand-yellow/15 text-brand-yellow",
  adherence: "bg-pink-500/15 text-pink-600 dark:text-pink-400",
  safety: "bg-red-500/15 text-red-600 dark:text-red-400",
};

export default function Progress() {
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/progress").then((r) => setData(r.data)); }, []);

  if (!data) return <div className="space-y-4"><Skeleton className="h-8 w-40" /><div className="grid grid-cols-2 gap-4">{[1,2,3,4].map(i=><Skeleton key={i} className="h-32 rounded-2xl" />)}</div></div>;

  const hrs = Math.floor(data.total_minutes / 60);
  const mins = data.total_minutes % 60;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-foreground" data-testid="progress-title">Progress</h1>
        {data.goal && <p className="text-muted-foreground mt-2">Goal: <span className="text-foreground font-medium">{data.goal}</span>{data.event && data.event !== "none" && ` · ${data.event}`}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Stat icon={Flame} label="Sessions done" value={data.completed_count} />
        <Stat icon={Target} label="Adherence" value={`${data.adherence}%`} sub="of planned sessions" />
        <Stat icon={Clock} label="Time trained" value={hrs > 0 ? `${hrs}h ${mins}m` : `${mins}m`} />
        <Stat icon={TrendingUp} label="Skipped" value={data.skipped_count} />
      </div>

      {data.chart.length > 0 && (
        <div className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-4">Weekly volume (min)</p>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.chart}>
              <XAxis dataKey="week" tickFormatter={(w) => fmtDate(w)} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
              <YAxis tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={30} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 12 }} />
              <Line type="monotone" dataKey="minutes" stroke="#EA8900" strokeWidth={3} dot={{ fill: "#EA8900", r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* What Caden has learned */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Brain size={18} className="text-brand-orange" />
          <h2 className="font-heading font-bold text-xl text-foreground">What Caden has learned about you</h2>
        </div>
        {data.insights.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border p-6 text-center text-muted-foreground" data-testid="progress-insights-empty">
            Nothing yet — the more you train and check in, the more Caden understands you.
          </div>
        ) : (
          <div className="space-y-3" data-testid="progress-insights">
            {[...data.insights].reverse().map((ins, i) => (
              <motion.div key={i} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.04 }}
                className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full shrink-0 ${CAT_COLOR[ins.category] || "bg-muted text-muted-foreground"}`}>
                  {ins.category || "note"}
                </span>
                <div>
                  <p className="text-foreground leading-relaxed text-[15px]">{ins.text}</p>
                  <p className="text-xs text-muted-foreground mt-1">confidence: {ins.confidence || "?"}</p>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
